<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import TemplatePreview from 'dashboard/components-next/template-preview/TemplatePreview.vue';
import InboxesAPI from 'dashboard/api/inboxes';
import whatsappTemplateLanguages from '../whatsappTemplateLanguages';
import {
  estimatedTemplatePrice,
  formatEstimatedPrice,
} from 'dashboard/helper/whatsappPricing';
import {
  buildTemplatePayload,
  emptyTemplateForm,
  extractVariables,
  renumberVariables,
  slugifyTemplateName,
  templateFormFromMeta,
} from './buildTemplatePayload';
import { checkTemplate, OPT_OUT_BUTTON_TEXT } from './templateRules';
import { TEMPLATE_LIBRARY, libraryTemplateForm } from './templateLibrary';

const props = defineProps({
  show: { type: Boolean, default: false },
  isSaving: { type: Boolean, default: false },
  inboxId: { type: Number, default: null },
  mode: { type: String, default: 'create' },
  initialTemplate: { type: Object, default: null },
  existingNames: { type: Array, default: () => [] },
});

const emit = defineEmits(['close', 'create', 'update']);

const { t } = useI18n();

const CATEGORIES = ['MARKETING', 'UTILITY', 'AUTHENTICATION'];
const BUTTON_TYPES = ['QUICK_REPLY', 'URL', 'PHONE_NUMBER', 'COPY_CODE'];
const HEADER_FORMATS = ['none', 'TEXT', 'IMAGE', 'VIDEO', 'DOCUMENT'];
const MB = 1024 * 1024;
const MEDIA_RULES = {
  IMAGE: { accept: 'image/jpeg,image/png', maxBytes: 5 * MB },
  VIDEO: { accept: 'video/mp4', maxBytes: 16 * MB },
  DOCUMENT: { accept: 'application/pdf', maxBytes: 100 * MB },
};

const form = reactive(emptyTemplateForm());
const bodyInput = ref(null);
const isUploading = ref(false);
const uploadedFileName = ref('');
const localMediaUrl = ref('');

const isEdit = computed(() => props.mode === 'edit');
const isAuthentication = computed(() => form.category === 'AUTHENTICATION');
const categoryLocked = computed(
  () => isEdit.value && props.initialTemplate?.status !== 'REJECTED'
);
const bodyVariables = computed(() => extractVariables(form.body));
const headerVariables = computed(() => extractVariables(form.headerText));
const mediaRule = computed(() => MEDIA_RULES[form.headerFormat]);

const titleKey = computed(() => {
  if (props.mode === 'edit') return 'TITLE_EDIT';
  if (props.mode === 'duplicate') return 'TITLE_DUPLICATE';
  return 'TITLE';
});

const duplicateName = name => {
  const base = name.replace(/_v\d+$/, '');
  let version = 2;
  while (props.existingNames.includes(`${base}_v${version}`)) version += 1;
  return `${base}_v${version}`;
};

const resetForm = () => {
  Object.assign(form, emptyTemplateForm());
  uploadedFileName.value = '';
  localMediaUrl.value = '';
  if (props.initialTemplate && props.mode !== 'create') {
    Object.assign(form, templateFormFromMeta(props.initialTemplate));
    if (props.mode === 'duplicate') form.name = duplicateName(form.name);
  }
};

watch(
  () => [props.show, props.mode, props.initialTemplate],
  () => {
    if (props.show) resetForm();
  },
  { immediate: true }
);

const applyLibraryTemplate = id => {
  Object.assign(form, libraryTemplateForm(id));
};

const onNameInput = event => {
  form.name = slugifyTemplateName(event.target.value);
};

const setHeaderFormat = format => {
  form.headerFormat = format;
  form.headerHandle = '';
  uploadedFileName.value = '';
  localMediaUrl.value = '';
  if (format !== 'TEXT') {
    form.headerText = '';
    form.headerExamples = {};
  }
};

const onHeaderFile = async event => {
  const file = event.target.files?.[0];
  if (!file) return;
  if (file.size > mediaRule.value.maxBytes) {
    useAlert(t('WHATSAPP_TEMPLATES.MANAGER.CREATE.HEADER_TOO_BIG'));
    return;
  }
  isUploading.value = true;
  try {
    const { data } = await InboxesAPI.uploadWhatsAppTemplateMedia(
      props.inboxId,
      file
    );
    form.headerHandle = data.handle;
    uploadedFileName.value = file.name;
    localMediaUrl.value =
      form.headerFormat === 'IMAGE' ? URL.createObjectURL(file) : '';
  } catch {
    useAlert(t('WHATSAPP_TEMPLATES.MANAGER.CREATE.HEADER_UPLOAD_ERROR'));
  } finally {
    isUploading.value = false;
  }
};

const addVariable = () => {
  const el = bodyInput.value;
  const position = el?.selectionStart ?? form.body.length;
  const token = `{{${bodyVariables.value.length + 1}}}`;
  const text = `${form.body.slice(0, position)}${token}${form.body.slice(position)}`;
  const renumbered = renumberVariables(text, form.bodyExamples);
  form.body = renumbered.text;
  form.bodyExamples = renumbered.examples;
  el?.focus();
};

const addButton = () => {
  form.buttons.push({
    type: 'QUICK_REPLY',
    text: '',
    url: '',
    urlExample: '',
    phoneNumber: '',
    example: '',
  });
};

const addOptOutButton = () => {
  form.buttons.push({
    type: 'QUICK_REPLY',
    text: OPT_OUT_BUTTON_TEXT,
    url: '',
    urlExample: '',
    phoneNumber: '',
    example: '',
  });
};

const removeButton = index => {
  form.buttons.splice(index, 1);
};

const checks = computed(() => checkTemplate(form));
const hasError = computed(() => checks.value.some(c => c.level === 'error'));
const isInvalid = computed(() => hasError.value || isUploading.value);
const canSuggestOptOut = computed(
  () =>
    form.category === 'MARKETING' &&
    form.buttons.length < 10 &&
    checks.value.some(c => c.code === 'MARKETING_WITHOUT_OPT_OUT')
);

const previewTemplate = computed(() => {
  if (isAuthentication.value) {
    return {
      name: form.name || 'template',
      language: form.language,
      category: form.category,
      components: [
        {
          type: 'BODY',
          text: t('WHATSAPP_TEMPLATES.MANAGER.CREATE.AUTH_PREVIEW_BODY'),
        },
      ],
    };
  }
  const payload = buildTemplatePayload({ ...form });
  const header = payload.components.find(c => c.type === 'HEADER');
  if (header?.example?.header_handle) {
    header.example.header_handle = [localMediaUrl.value];
  }
  return payload;
});

const previewVariables = computed(() => ({
  ...form.bodyExamples,
  ...form.headerExamples,
}));

const priceFor = category => {
  const price = estimatedTemplatePrice(category);
  if (price === null) return '';
  return t('WHATSAPP_TEMPLATES.MANAGER.PRICING.PER_MESSAGE', {
    price: formatEstimatedPrice(price),
  });
};

const submit = () => {
  const payload = buildTemplatePayload({ ...form });
  if (isEdit.value) {
    emit('update', { name: form.name, language: form.language, payload });
  } else {
    emit('create', payload);
  }
};
</script>

<template>
  <woot-modal :show="show" size="medium" :on-close="() => emit('close')">
    <woot-modal-header
      :header-title="$t(`WHATSAPP_TEMPLATES.MANAGER.CREATE.${titleKey}`)"
      :header-content="$t('WHATSAPP_TEMPLATES.MANAGER.CREATE.DESCRIPTION')"
    />
    <div class="grid grid-cols-1 gap-6 p-6 lg:grid-cols-2">
      <div class="flex flex-col gap-4">
        <div v-if="mode === 'create'" class="flex flex-col gap-2">
          <span class="text-sm font-medium">
            {{ $t('WHATSAPP_TEMPLATES.MANAGER.LIBRARY.TITLE') }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{ $t('WHATSAPP_TEMPLATES.MANAGER.LIBRARY.HINT') }}
          </span>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="item in TEMPLATE_LIBRARY"
              :key="item.id"
              type="button"
              class="rounded-md border border-n-weak px-2 py-1 text-xs text-n-slate-12 hover:bg-n-alpha-2"
              @click="applyLibraryTemplate(item.id)"
            >
              {{ $t(`WHATSAPP_TEMPLATES.MANAGER.LIBRARY.${item.id}.TITLE`) }}
            </button>
          </div>
        </div>

        <label class="block">
          <span class="mb-1 block text-sm font-medium">
            {{ $t('WHATSAPP_TEMPLATES.MANAGER.CREATE.NAME_LABEL') }}
          </span>
          <input
            :value="form.name"
            type="text"
            class="w-full"
            :disabled="isEdit"
            :placeholder="
              $t('WHATSAPP_TEMPLATES.MANAGER.CREATE.NAME_PLACEHOLDER')
            "
            @input="onNameInput"
          />
        </label>

        <div class="flex flex-col gap-2">
          <span class="text-sm font-medium">
            {{ $t('WHATSAPP_TEMPLATES.MANAGER.CREATE.CATEGORY_LABEL') }}
          </span>
          <div class="grid grid-cols-1 gap-2 sm:grid-cols-3">
            <label
              v-for="category in CATEGORIES"
              :key="category"
              class="flex min-w-0 cursor-pointer flex-col gap-1 rounded-md border p-2"
              :class="
                form.category === category
                  ? 'border-n-brand bg-n-alpha-2'
                  : 'border-n-weak'
              "
            >
              <span class="flex items-center gap-2 text-sm font-medium">
                <input
                  v-model="form.category"
                  type="radio"
                  name="template-category"
                  class="!mb-0"
                  :value="category"
                  :disabled="categoryLocked"
                />
                {{ $t(`WHATSAPP_TEMPLATES.MANAGER.CATEGORY.${category}`) }}
              </span>
              <span class="text-xs text-n-slate-11">
                {{
                  $t(
                    `WHATSAPP_TEMPLATES.MANAGER.CATEGORY_GUIDE.${category}.PURPOSE`
                  )
                }}
              </span>
              <span class="text-xs italic text-n-slate-11">
                {{
                  $t(
                    `WHATSAPP_TEMPLATES.MANAGER.CATEGORY_GUIDE.${category}.EXAMPLE`
                  )
                }}
              </span>
              <span class="text-xs text-n-slate-11">{{
                priceFor(category)
              }}</span>
            </label>
          </div>
          <span class="text-xs text-n-slate-11">
            {{ $t('WHATSAPP_TEMPLATES.MANAGER.CATEGORY_GUIDE.META_DECIDES') }}
          </span>
        </div>

        <label class="block">
          <span class="mb-1 block text-sm font-medium">
            {{ $t('WHATSAPP_TEMPLATES.MANAGER.CREATE.LANGUAGE_LABEL') }}
          </span>
          <select v-model="form.language" class="w-full" :disabled="isEdit">
            <option
              v-for="language in whatsappTemplateLanguages"
              :key="language.id"
              :value="language.id"
            >
              {{ language.name }}
            </option>
          </select>
        </label>
        <span v-if="isEdit" class="text-xs text-n-slate-11">
          {{ $t('WHATSAPP_TEMPLATES.MANAGER.CREATE.LOCKED_FIELD_HINT') }}
        </span>

        <template v-if="!isAuthentication">
          <label class="block">
            <span class="mb-1 block text-sm font-medium">
              {{ $t('WHATSAPP_TEMPLATES.MANAGER.CREATE.HEADER_FORMAT') }}
            </span>
            <select
              :value="form.headerFormat"
              class="w-full"
              @change="setHeaderFormat($event.target.value)"
            >
              <option
                v-for="format in HEADER_FORMATS"
                :key="format"
                :value="format"
              >
                {{
                  $t(
                    `WHATSAPP_TEMPLATES.MANAGER.CREATE.HEADER_FORMATS.${format.toUpperCase()}`
                  )
                }}
              </option>
            </select>
          </label>
          <input
            v-if="form.headerFormat === 'TEXT'"
            v-model="form.headerText"
            type="text"
            maxlength="60"
            class="w-full"
            :placeholder="
              $t('WHATSAPP_TEMPLATES.MANAGER.CREATE.HEADER_PLACEHOLDER')
            "
          />
          <div v-if="headerVariables.length" class="flex flex-col gap-2">
            <input
              v-for="variable in headerVariables"
              :key="`header-${variable}`"
              v-model="form.headerExamples[variable]"
              type="text"
              class="w-full"
              :placeholder="
                $t('WHATSAPP_TEMPLATES.MANAGER.CREATE.EXAMPLE_PLACEHOLDER', {
                  variable,
                })
              "
            />
          </div>
          <div v-if="mediaRule" class="flex flex-col gap-1">
            <label class="text-sm font-medium">
              {{ $t('WHATSAPP_TEMPLATES.MANAGER.CREATE.HEADER_UPLOAD') }}
              <input
                type="file"
                class="mt-1 block w-full"
                :accept="mediaRule.accept"
                :disabled="!inboxId || isUploading"
                @change="onHeaderFile"
              />
            </label>
            <span v-if="isUploading" class="text-xs text-n-slate-11">
              {{ $t('WHATSAPP_TEMPLATES.MANAGER.CREATE.HEADER_UPLOADING') }}
            </span>
            <span v-else-if="uploadedFileName" class="text-xs text-n-slate-11">
              {{ uploadedFileName }}
            </span>
          </div>

          <label class="block">
            <div class="mb-1 flex items-center justify-between">
              <span class="text-sm font-medium">
                {{ $t('WHATSAPP_TEMPLATES.MANAGER.CREATE.BODY_LABEL') }}
              </span>
              <button
                type="button"
                class="text-xs text-n-brand"
                @click="addVariable"
              >
                {{ $t('WHATSAPP_TEMPLATES.MANAGER.CREATE.INSERT_VARIABLE') }}
              </button>
            </div>
            <textarea
              ref="bodyInput"
              v-model="form.body"
              rows="6"
              maxlength="1024"
              class="w-full"
              :placeholder="
                $t('WHATSAPP_TEMPLATES.MANAGER.CREATE.BODY_PLACEHOLDER')
              "
            />
          </label>
          <div v-if="bodyVariables.length" class="flex flex-col gap-2">
            <span class="text-xs text-n-slate-11">
              {{ $t('WHATSAPP_TEMPLATES.MANAGER.CREATE.EXAMPLE_HINT') }}
            </span>
            <input
              v-for="variable in bodyVariables"
              :key="`body-${variable}`"
              v-model="form.bodyExamples[variable]"
              type="text"
              class="w-full"
              :placeholder="
                $t('WHATSAPP_TEMPLATES.MANAGER.CREATE.EXAMPLE_PLACEHOLDER', {
                  variable,
                })
              "
            />
          </div>

          <label class="block">
            <span class="mb-1 block text-sm font-medium">
              {{ $t('WHATSAPP_TEMPLATES.MANAGER.CREATE.FOOTER_LABEL') }}
            </span>
            <input
              v-model="form.footer"
              type="text"
              maxlength="60"
              class="w-full"
              :placeholder="
                $t('WHATSAPP_TEMPLATES.MANAGER.CREATE.FOOTER_PLACEHOLDER')
              "
            />
          </label>

          <div class="flex flex-col gap-2">
            <div class="flex items-center justify-between">
              <span class="text-sm font-medium">
                {{ $t('WHATSAPP_TEMPLATES.MANAGER.CREATE.BUTTONS_LABEL') }}
              </span>
              <button
                type="button"
                class="text-xs text-n-brand"
                :disabled="form.buttons.length >= 10"
                @click="addButton"
              >
                {{ $t('WHATSAPP_TEMPLATES.MANAGER.CREATE.ADD_BUTTON') }}
              </button>
            </div>
            <button
              v-if="canSuggestOptOut"
              type="button"
              class="self-start text-xs text-n-brand"
              @click="addOptOutButton"
            >
              {{ $t('WHATSAPP_TEMPLATES.MANAGER.CREATE.ADD_OPT_OUT_BUTTON') }}
            </button>
            <div
              v-for="(button, index) in form.buttons"
              :key="index"
              class="flex flex-col gap-2 rounded-md border border-n-weak p-2"
            >
              <div class="flex items-center gap-2">
                <select v-model="button.type" class="!mb-0 w-40">
                  <option
                    v-for="type in BUTTON_TYPES"
                    :key="type"
                    :value="type"
                  >
                    {{
                      $t(
                        `WHATSAPP_TEMPLATES.MANAGER.CREATE.BUTTON_TYPES.${type}`
                      )
                    }}
                  </option>
                </select>
                <input
                  v-if="button.type !== 'COPY_CODE'"
                  v-model="button.text"
                  type="text"
                  maxlength="25"
                  class="!mb-0 min-w-0 flex-1"
                  :placeholder="
                    $t(
                      'WHATSAPP_TEMPLATES.MANAGER.CREATE.BUTTON_TEXT_PLACEHOLDER'
                    )
                  "
                />
                <input
                  v-else
                  v-model="button.example"
                  type="text"
                  maxlength="15"
                  class="!mb-0 min-w-0 flex-1"
                  :placeholder="
                    $t(
                      'WHATSAPP_TEMPLATES.MANAGER.CREATE.COPY_CODE_PLACEHOLDER'
                    )
                  "
                />
                <woot-button
                  variant="clear"
                  color-scheme="alert"
                  icon="dismiss"
                  size="small"
                  @click="removeButton(index)"
                />
              </div>
              <input
                v-if="button.type === 'URL'"
                v-model="button.url"
                type="text"
                class="!mb-0 w-full"
                :placeholder="
                  $t('WHATSAPP_TEMPLATES.MANAGER.CREATE.BUTTON_URL_PLACEHOLDER')
                "
              />
              <input
                v-if="
                  button.type === 'URL' && extractVariables(button.url).length
                "
                v-model="button.urlExample"
                type="text"
                class="!mb-0 w-full"
                :placeholder="
                  $t(
                    'WHATSAPP_TEMPLATES.MANAGER.CREATE.BUTTON_URL_EXAMPLE_PLACEHOLDER'
                  )
                "
              />
              <input
                v-if="button.type === 'PHONE_NUMBER'"
                v-model="button.phoneNumber"
                type="text"
                class="!mb-0 w-full"
                :placeholder="
                  $t(
                    'WHATSAPP_TEMPLATES.MANAGER.CREATE.BUTTON_PHONE_PLACEHOLDER'
                  )
                "
              />
            </div>
          </div>
        </template>

        <template v-else>
          <label class="flex items-center gap-2">
            <input v-model="form.addSecurityRecommendation" type="checkbox" />
            <span class="text-sm">
              {{ $t('WHATSAPP_TEMPLATES.MANAGER.CREATE.AUTH_SECURITY_LABEL') }}
            </span>
          </label>
          <label class="block">
            <span class="mb-1 block text-sm font-medium">
              {{
                $t('WHATSAPP_TEMPLATES.MANAGER.CREATE.AUTH_EXPIRATION_LABEL')
              }}
            </span>
            <input
              v-model.number="form.codeExpirationMinutes"
              type="number"
              min="1"
              max="90"
              class="w-full"
            />
          </label>
        </template>
      </div>

      <div class="flex flex-col gap-3">
        <span class="text-sm font-medium">
          {{ $t('WHATSAPP_TEMPLATES.MANAGER.CREATE.PREVIEW_LABEL') }}
        </span>
        <div class="rounded-lg bg-n-alpha-black2 p-4">
          <TemplatePreview
            :template="previewTemplate"
            :variables="previewVariables"
            platform="whatsapp"
          />
        </div>
        <div class="flex flex-col gap-1 rounded-md border border-n-weak p-3">
          <span class="text-sm font-medium">
            {{ $t('WHATSAPP_TEMPLATES.MANAGER.CREATE.CHECKS_TITLE') }}
          </span>
          <span v-if="!checks.length" class="text-xs text-n-teal-11">
            {{ $t('WHATSAPP_TEMPLATES.MANAGER.CREATE.CHECKS_OK') }}
          </span>
          <ul v-else class="m-0 flex list-none flex-col gap-1 p-0">
            <li
              v-for="(check, index) in checks"
              :key="`${check.code}-${index}`"
              class="flex items-start gap-2 text-xs"
              :class="
                check.level === 'error' ? 'text-n-ruby-11' : 'text-n-amber-11'
              "
            >
              <span
                class="mt-0.5 size-4 shrink-0"
                :class="
                  check.level === 'error'
                    ? 'i-lucide-circle-x'
                    : 'i-lucide-triangle-alert'
                "
              />
              <span>{{
                $t(
                  `WHATSAPP_TEMPLATES.MANAGER.RULES.${check.code}`,
                  check.params
                )
              }}</span>
            </li>
          </ul>
        </div>
        <span class="text-xs text-n-slate-11">
          {{ $t('WHATSAPP_TEMPLATES.MANAGER.CREATE.APPROVAL_HINT') }}
        </span>
      </div>
    </div>
    <div class="flex justify-end gap-2 px-6 pb-6">
      <woot-button variant="clear" @click="emit('close')">
        {{ $t('WHATSAPP_TEMPLATES.MANAGER.CREATE.CANCEL') }}
      </woot-button>
      <woot-button
        :is-loading="isSaving"
        :is-disabled="isInvalid"
        @click="submit"
      >
        {{
          isEdit
            ? $t('WHATSAPP_TEMPLATES.MANAGER.CREATE.SUBMIT_EDIT')
            : $t('WHATSAPP_TEMPLATES.MANAGER.CREATE.SUBMIT')
        }}
      </woot-button>
    </div>
  </woot-modal>
</template>
